import { useMemo } from 'react';

import { VisitHistoryMedicalDocument, VisitHistoryMedicalDocumentQuery } from '@dreryk/egabinet-visit-frontend-api';

import {
   LazyLoadedDataConfigurationByCqrsQuery,
   useLazyLoadedDataConfigurationByCqrsQuery,
} from '@dreryk/drerykweb-main-frontend';
import { createTranslatedText, dateTimeFormatter } from '@dreryk/react-common';
import {
   AlignItems,
   createDynamicColumnsWidth,
   DownloadIcon,
   DownloadLink,
   EmptyStatePlaceholder,
   IconFillColor,
   JustifyContent,
   Table,
   TableColumnConfiguration,
   ValueRendererProps,
} from '@dreryk/react-components';

import { visitHistoryTranslationKeys } from '../translations';

const useVisitHistoryMedicalDocumentsDataConfiguration = (
   visitId: number
): LazyLoadedDataConfigurationByCqrsQuery<VisitHistoryMedicalDocument> => {
   return useLazyLoadedDataConfigurationByCqrsQuery<VisitHistoryMedicalDocument>(() => {
      const query = new VisitHistoryMedicalDocumentQuery();
      query.visitId = visitId;
      return query;
   });
};

const useVisitHistoryMedicalDocumentColumnsConfiguration = (): TableColumnConfiguration<VisitHistoryMedicalDocument>[] => {
   return useMemo<TableColumnConfiguration<VisitHistoryMedicalDocument>[]>(() => {
      const columnsWidth = createDynamicColumnsWidth({
         typeName: '100%',
         creationDate: '156px',
         url: '48px',
      });

      return [
         {
            name: 'typeName',
            label: visitHistoryTranslationKeys.medicalDocumentsTable.typeName,
            width: columnsWidth.typeName,
            sortingDisabled: true,
            justifyContent: JustifyContent.START,
            alignItems: AlignItems.CENTER,
            accessor: 'typeName',
         },
         {
            name: 'creationDate',
            label: visitHistoryTranslationKeys.medicalDocumentsTable.creationDate,
            width: columnsWidth.creationDate,
            sortingDisabled: true,
            justifyContent: JustifyContent.START,
            alignItems: AlignItems.CENTER,
            accessor: 'creationDate',
            formatter: dateTimeFormatter,
         },
         {
            name: 'url',
            label: createTranslatedText(''),
            width: columnsWidth.url,
            sortingDisabled: true,
            justifyContent: JustifyContent.CENTER,
            alignItems: AlignItems.CENTER,
            accessor: 'url',

            renderer: ({ value: url }: ValueRendererProps<VisitHistoryMedicalDocument, string>): JSX.Element | null => {
               return (
                  <DownloadLink
                     testId={'downloadMedicalDocument'}
                     url={url}
                     icon={<DownloadIcon fillColor={IconFillColor.PRIMARY} testId={'download'} />}
                  />
               );
            },
         },
      ];
   }, []);
};

type VisitHistoryMedicalDocumentsTableProps = {
   readonly visitId: number;
};

export const VisitHistoryMedicalDocumentsTable = (props: VisitHistoryMedicalDocumentsTableProps): JSX.Element => {
   return (
      <Table
         testId={'medicalDocuments'}
         dataConfiguration={useVisitHistoryMedicalDocumentsDataConfiguration(props.visitId)}
         columns={useVisitHistoryMedicalDocumentColumnsConfiguration()}
         keyExtractor={'id'}
         pageSize={1000}
         placeholder={
            <EmptyStatePlaceholder
               testId={'medicalDocuments'}
               heading={visitHistoryTranslationKeys.medicalDocumentsTable.noMedicalDocuments}
               subheading={visitHistoryTranslationKeys.medicalDocumentsTable.noMedicalDocumentsWereIssued}
            />
         }
      />
   );
};
